import Label from '../../ui/label';
import { Spinner } from '../../../assets/icon/svg';
import { useMutation } from '../../../hooks/services/mutations/use-mutation';
import { useDispatch, useSelector } from 'react-redux';
import { selectUpdatePassword, setUpdatePasswordValue } from '../../../features/it-user-forms/user-forms-slice';
import { useCreateUser } from '../../../hooks/actions/use-create-user';
import { PasswordFormState } from '../../../features/it-user-forms/user-form-state';
import { customStyle, defaultStyle } from '../../../constant/custom-style';
import Input from '../../ui/input';
import ConfirmDefaultModal from '../../global/modal/confirm-default-modal';
import { useState } from 'react';

export default function UpdatePassword() {
    
    const dispatch = useDispatch()
    const { isLoadingUpdateProfile } = useCreateUser()
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const updatePassword = useSelector(selectUpdatePassword);
    const hasValue = !!updatePassword.values.password && !!updatePassword.values.newPassword && !!updatePassword.values.confirmPassword;
    const { handleCancelChangePassword, handlePasswordVisibility, handleUpdateChangePassword } = useCreateUser()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        dispatch( setUpdatePasswordValue({
            field: name as keyof PasswordFormState["values"],
            value: value,
        }));
    };

    return (
        <div className="">
            <div className='rounded-xl border border-gray-200 bg-white'>
                <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                    <div>
                        <p className="text-sm font-semibold text-gray-800">
                            Change Password
                        </p>
                        <p className="mt-0.5 text-[11px] text-gray-500">
                            Keep your account secure with a strong password.
                        </p>
                    </div>
                </div>
                <div className='p-3 space-y-4'>
                    <div className="bg-white rounded-xl border">
                        <div className=" bg-gray-50 rounded-t-xl px-3 py-2">
                            <p className="text-xs font-semibold text-gray-700">
                                Password Information
                            </p>
                            <p className="mt-0.5 text-[10px] text-gray-400">
                                Update your password to help keep your account secure.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <Label 
                                    label="Current Password" 
                                    fontSize="text-xs mb-1" 
                                    isRequired={true} 
                                    requiredText=" *" 
                                />
                                <div className="relative">
                                    <Input
                                        type={updatePassword.visible.password ? "text" : "password"}
                                        name="password"
                                        value={updatePassword.values.password}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} pr-10 ${!updatePassword.values.password ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter current password"
                                        disabled={isLoadingUpdateProfile}
                                    />
                                    {updatePassword.values.password.length > 0 && (
                                        <button
                                            type="button"
                                            disabled={isLoadingUpdateProfile}
                                            onClick={() => handlePasswordVisibility("password")}
                                            aria-label={updatePassword.visible.password ? "Hide password" : "Show password"}
                                            title={updatePassword.visible.password ? "Hide password" : "Show password"}
                                            className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center p-1.5 text-gray-400 transition-all duration-200  hover:text-[#186cb2] active:scale-90 outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {updatePassword.visible.password ? (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-[18px] w-[18px]"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M3.5 12s3.2-6 8.5-6 8.5 6 8.5 6-3.2 6-8.5 6-8.5-6-8.5-6Z"
                                                    />
                                                    <circle cx="12" cy="12" r="2.5" />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="m4 4 16 16"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-[18px] w-[18px]"
                                                >
                                                    <rect
                                                        x="4"
                                                        y="10"
                                                        width="16"
                                                        height="10"
                                                        rx="2"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M8 10V7a4 4 0 0 1 8 0v3"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M12 14v2"
                                                    />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <Label 
                                    label="New Password" 
                                    fontSize="text-xs mb-1" 
                                    isRequired={true} 
                                    requiredText=" *" 
                                />
                                <div className="relative">
                                    <Input
                                        type={updatePassword.visible.newPassword ? "text" : "password"}
                                        name="newPassword"
                                        value={updatePassword.values.newPassword}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} pr-10 ${!updatePassword.values.newPassword ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter current password"
                                        disabled={isLoadingUpdateProfile}
                                    />
                                    {updatePassword.values.password.length > 0 && (
                                        <button
                                            type="button"
                                            disabled={isLoadingUpdateProfile}
                                            onClick={() => handlePasswordVisibility("password")}
                                            aria-label={updatePassword.visible.newPassword ? "Hide password" : "Show password"}
                                            title={updatePassword.visible.newPassword ? "Hide password" : "Show password"}
                                            className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center p-1.5 text-gray-400 transition-all duration-200  hover:text-[#186cb2] active:scale-90 outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {updatePassword.visible.newPassword ? (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-[18px] w-[18px]"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M3.5 12s3.2-6 8.5-6 8.5 6 8.5 6-3.2 6-8.5 6-8.5-6-8.5-6Z"
                                                    />
                                                    <circle cx="12" cy="12" r="2.5" />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="m4 4 16 16"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-[18px] w-[18px]"
                                                >
                                                    <rect
                                                        x="4"
                                                        y="10"
                                                        width="16"
                                                        height="10"
                                                        rx="2"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M8 10V7a4 4 0 0 1 8 0v3"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M12 14v2"
                                                    />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="md:col-span-2 lg:col-span-1">
                                <Label 
                                    label="Confirm New Password" 
                                    fontSize="text-xs mb-1" 
                                    isRequired={true} 
                                    requiredText=" *" 
                                />
                                <div className="relative">
                                    <Input
                                        type={updatePassword.visible.confirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={updatePassword.values.confirmPassword}
                                        isCustomStyle={true}
                                        customCss={`${defaultStyle} pr-10 ${!updatePassword.values.confirmPassword ? '' : customStyle}`}
                                        onChange={handleChange}
                                        required={true}
                                        placeholder="Enter current password"
                                        disabled={isLoadingUpdateProfile}
                                    />
                                    {updatePassword.values.password.length > 0 && (
                                        <button
                                            type="button"
                                            disabled={isLoadingUpdateProfile}
                                            onClick={() => handlePasswordVisibility("password")}
                                            aria-label={updatePassword.visible.confirmPassword ? "Hide password" : "Show password"}
                                            title={updatePassword.visible.confirmPassword ? "Hide password" : "Show password"}
                                            className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center p-1.5 text-gray-400 transition-all duration-200  hover:text-[#186cb2] active:scale-90 outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {updatePassword.visible.confirmPassword ? (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-[18px] w-[18px]"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M3.5 12s3.2-6 8.5-6 8.5 6 8.5 6-3.2 6-8.5 6-8.5-6-8.5-6Z"
                                                    />
                                                    <circle cx="12" cy="12" r="2.5" />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="m4 4 16 16"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-[18px] w-[18px]"
                                                >
                                                    <rect
                                                        x="4"
                                                        y="10"
                                                        width="16"
                                                        height="10"
                                                        rx="2"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M8 10V7a4 4 0 0 1 8 0v3"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        d="M12 14v2"
                                                    />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='p-3'>
                            <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2.5">
                                <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-yellow-200 text-[10px] font-bold text-yellow-800">
                                    !
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold text-yellow-800">
                                        Password requirements
                                    </p>
                                    <p className="mt-0.5 text-[11px] leading-relaxed text-yellow-700">
                                        Password must contain at least 8 characters, including a lowercase letter, an uppercase letter, and a number.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-end">
                <button
                    type="button"
                    disabled={isLoadingUpdateProfile}
                    onClick={handleCancelChangePassword}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-3.5 w-3.5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 6l12 12M18 6 6 18"
                        />
                    </svg>

                    <span>Cancel</span>
                </button>

                <button
                    type="button"
                    disabled={!hasValue || isLoadingUpdateProfile}
                    onClick={() => setShowConfirmModal(true)}
                    className={`flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white shadow-sm transition active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#186cb2]/20 disabled:cursor-not-allowed disabled:opacity-50 ${!hasValue || isLoadingUpdateProfile ? "bg-gray-400" : "bg-[#186cb2] hover:bg-[#155d9b]"}`}
                >
                    {isLoadingUpdateProfile ? (
                        <>
                            <Spinner size={13} />
                            <span>Updating...</span>
                        </>
                    ) : (
                        <>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                className="h-3.5 w-3.5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 3 5 6v5c0 4.5 2.9 8.3 7 9.5 4.1-1.2 7-5 7-9.5V6l-7-3Z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m9.5 12 1.7 1.7 3.5-3.5"
                                />
                            </svg>

                            <span>Update Password</span>
                        </>
                    )}
                </button>
            </div>
            {showConfirmModal && (
                <ConfirmDefaultModal
                    modalTitle="Confirm Password Change"
                    modalSubTitle="Are you sure you want to change your password? You will need to use your new password the next time you sign in."
                    onCancel={() => setShowConfirmModal(false)}
                    onConfirm={handleUpdateChangePassword}
                    isLoading={isLoadingUpdateProfile}
                />
            )}
        </div>
    )
}